import { ApiStage, delay, EthAddress } from '@xylabs/sdk-js'
import axios from 'axios'

import { XyoWorldBase } from '../Base'
import { XyoWorldEntitySettingsJson } from '../models'

export class XyoWorldEntitySettingsClient extends XyoWorldBase<XyoWorldEntitySettingsJson> {
  private static apiUrls: { [key: string]: string } = {
    dev: 'https://dev-api.world.xyo.network/entity/',
    localhost: 'http://localhost:3030/dev/entity/',
    prod: 'https://api.world.xyo.network/entity/',
  }

  private static cache: Record<string, XyoWorldEntitySettingsClient> = {}

  public data: XyoWorldEntitySettingsJson

  private address: EthAddress

  private api: string

  private authToken?: string | null

  private loading = false

  private constructor(address: EthAddress, authToken: string | null, api: ApiStage, jsonObject?: XyoWorldEntitySettingsJson) {
    super()
    this.address = address
    this.data = {
      address: address.toLowerCaseString(),
      name: address.toString(),
    }
    this.api = api
    this.authToken = authToken
    this.loadJsonObject(jsonObject)
  }

  public static async fetch(address: EthAddress, authToken: string | null = null, api = ApiStage.Prod, jsonObject?: XyoWorldEntitySettingsJson) {
    const key = address.toString()
    const entity = new XyoWorldEntitySettingsClient(address, authToken, api, jsonObject)
    this.cache[key] = await entity.load()
    return this.cache[key]
  }

  public static async get(address: EthAddress, authToken: string | null = null, api = ApiStage.Prod, jsonObject?: XyoWorldEntitySettingsJson) {
    const key = address.toString()
    return this.cache[key] ?? (await this.fetch(address, authToken, api, jsonObject))
  }

  public endPoint() {
    return `${XyoWorldEntitySettingsClient.apiUrls[this.api]}${this.address.toLowerCaseString()}`
  }

  public async load() {
    if (!this.loading) {
      this.loading = true
      try {
        const response = await axios.get<XyoWorldEntitySettingsJson>(this.endPoint(), {
          headers: this.authToken ? { Authorization: this.authToken } : {},
        })
        this.data = response.data
      } finally {
        this.loading = false
      }
    } else {
      while (this.loading) {
        await delay(100)
      }
    }
    return this
  }

  loadJsonObject(jsonObject?: XyoWorldEntitySettingsJson) {
    this.data = jsonObject ?? { address: this.address.toLowerCaseString() }
    return this
  }

  public async save() {
    const postData: XyoWorldEntitySettingsJson = {
      ...this.data,
    }

    const result = await axios.post<XyoWorldEntitySettingsJson>(this.endPoint(), postData, {
      headers: this.authToken ? { Authorization: this.authToken } : {},
    })
    return result.data
  }

  public async setAdmin(admin: boolean) {
    const postData: XyoWorldEntitySettingsJson = {
      _admin: admin,
      address: this.data.address,
    }

    const result = await axios.post<XyoWorldEntitySettingsJson>(`${this.endPoint()}/admin`, postData, {
      headers: this.authToken ? { Authorization: this.authToken } : {},
    })
    return result.data
  }

  toJsonObject(): XyoWorldEntitySettingsJson {
    return this.data
  }
}
